import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { canonicalJson, hashCanonicalJson, sha256Hex } from './reportContractHash';
import { extractSections } from '../audit/reportTextExtract';
import { containsTruthViolation } from '../audit/qaSeverityPolicy';
import { buildSentimentScreeningFindings, publicSentimentScreeningReadiness, type SentimentScreeningReceipt } from '../sentiment/publicSentimentScreening';
import { checkPublicSentimentAssertions } from '../grounding/publicSentimentAssertions';
import { verifyWaterUseScreeningClaims } from '../audit/waterUseScreeningGuard';
import { extractAbsenceAssertionCandidates } from '../audit/absenceAssertionDetector';
import { checkDocumentFactCitations } from './documentFactCitations';
import { buildGroundedSentimentReport } from '../sentiment/buildGroundedSentimentReport';
import { qualifiedLocalSentimentRows } from '../sentiment/qualifiedLocalSentiment';
import { normalizeStoredConclusion } from '../resolveEffectiveConclusion';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { admitEvidenceSources, type SourceAdmissionInput } from './reportEvidenceSourceAdmission';

/** Attests a completed deterministic source/coverage product, never a model
 * verdict or a favorable community assessment. The immutable report source
 * stores this receipt; any later narrative/reference/scope change invalidates it. */
export interface DeterministicScreeningValidation {
  schemaVersion: 'deterministic-screening-validation-v1';
  method: 'source-bound-no-local-stance-v1' | 'source-bound-local-stance-v1';
  reportId: string;
  source: { sedId: string; sedVersion: number; snapshotHash: string };
  scanHash: string;
  /** Separately binds worker research while source.snapshotHash still pins canonical SED. */
  researchHash?: string;
  contentHash: string;
  /** Absent on legacy receipts, whose original insertion-order hash remains strict. */
  contentHashVersion?: 'canonical-section-text-v2' | 'canonical-section-text-v3';
  checkedAt: string;
}
const hash = /^[a-f0-9]{64}$/;
const stanceFields = ['perBtmSentiment', 'themeDeepDives', 'trendAnalysis', 'comparableSiteBenchmark'] as const;

/** Completion proves the cited sources were available, not merely that a
 * URL occupied each slot. Unused failed attempts remain private receipts. */
function citedSourcesAreAdmitted(
  report: Partial<SiteRagReport>,
  publicSentiment: SourceAdmissionInput['publicSentiment'] = report.publicSentimentResearch?.section,
): boolean {
  const admissions = admitEvidenceSources({ references: report.references,
    sourceCoverage: report.screeningBasis?.sourceCoverage, publicSentiment });
  const cited = extractSections(report as SiteRagReport).flatMap(section =>
    [...section.content.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))]
      .flatMap(token => (token[0].match(/\d+/g) ?? []).map(Number)));
  for (const claim of report.claims ?? []) for (const source of claim.sources ?? []) {
    const match = /^ref:(\d+)$/i.exec(source);
    if (match) cited.push(Number(match[1]));
  }
  return cited.every(index => {
    const row = admissions[index - 1];
    const ref = row?.reference;
    return Number.isSafeInteger(index) && index > 0 && row?.admitted === true && !!ref?.url
      && (ref.field !== 'documents' || ref.url === `doc://${ref.documentId}`);
  });
}

function contentHash(
  report: Partial<SiteRagReport>,
  version?: DeterministicScreeningValidation['contentHashVersion'],
): string {
  // Storage serializes canonical JSON. Text extraction includes object labels
  // and therefore must see that same key order before hashing; otherwise a
  // benign JSON round trip changes the attested text of structured findings.
  const textSource = version === 'canonical-section-text-v2' || version === 'canonical-section-text-v3'
    ? JSON.parse(canonicalJson(report)) as SiteRagReport
    : report as SiteRagReport;
  return hashCanonicalJson({
    reportId: report.id, reportType: report.reportType,
    sedId: report.sedId, sedVersion: report.screeningBasis?.sed.version,
    sections: extractSections(textSource), references: report.references,
    claims: report.claims, structuredFindings: report.structuredFindings,
    screeningBasis: report.screeningBasis, studyAreaScope: report.studyAreaScope,
    ...(report.publicSentimentResearch ? { publicSentimentResearch: report.publicSentimentResearch } : {}),
    publicSentimentStatus: report.publicSentimentStatus,
    publicSentimentMeta: report.publicSentimentMeta, sentimentIndex: report.sentimentIndex,
    datasets: report.datasets,
    overallScore: report.overallScore, overallStatus: report.overallStatus,
    ...(version === 'canonical-section-text-v3' ? { conclusion: report.conclusion } : {}),
  });
}

/** Rebuild every assessed chapter from the admitted frozen research. A copied
 * deterministic flag, changed stance or arbitrary prose cannot attest itself. */
function assessedConstructionMatches(report: Partial<SiteRagReport>): boolean {
  const research = report.publicSentimentResearch;
  if (!research || research.version !== 'psr-grounded-research-v1' || research.proseSource !== 'deterministic'
    || !Number.isFinite(report.overallScore) || report.publicSentimentStatus === 'unavailable') return false;
  const rows = qualifiedLocalSentimentRows(research.section);
  const observedIndices = research.section.entries.map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry.scope === 'site' && entry.assessmentStatus === 'assessed').map(({ index }) => index);
  // This is the collector's exact existing fallback; a deterministic label
  // cannot authorize arbitrary prose even when its chapters are rebuilt.
  const deterministicProse = observedIndices.map(index => ({ entry: research.section.entries[index]!, index }))
    .filter(({ entry }) => entry.supportingSnippet.split(/\s+/).length <= 25).slice(0, 3)
    .map(({ entry, index }) => `“${entry.supportingSnippet}” [PS ${index + 1}]`).join('\n\n');
  if (!rows.length || rows.length !== research.section.assessment?.localObservationCount
    || observedIndices.length !== rows.length || rows.some(({ index }, position) => index !== observedIndices[position])
    || research.prose !== deterministicProse
    || report.sentimentIndex?.score !== research.section.assessment?.supportIndex
    || rows.some(({ entry, index }) => {
      const original = research.section.entries[index];
      return !original || original.sentimentScore !== entry.sentimentScore || original.confidence !== entry.confidence
        || original.scope !== entry.scope || original.assessmentStatus !== entry.assessmentStatus
        || original.observedDate !== entry.observedDate || original.dateBasis !== entry.dateBasis || original.btmTech !== entry.btmTech;
    })
    || (report.publicSentimentMeta?.assessment
      && hashCanonicalJson(report.publicSentimentMeta.assessment) !== hashCanonicalJson(research.section.assessment))) return false;
  try {
    const rebuilt = buildGroundedSentimentReport(research, report.references ?? []);
    const data = rebuilt.data;
    const fields = ['executive', 'executiveSummary', 'summary', 'stakeholderMap', 'perBtmSentiment',
      'themeDeepDives', 'trendAnalysis', 'comparableSiteBenchmark', 'mitigationPlaybook', 'claims'] as const;
    return fields.every(key => hashCanonicalJson(report[key]) === hashCanonicalJson(data[key]))
      && report.sentimentAppendix === data.appendix
      && hashCanonicalJson(report.conclusion) === hashCanonicalJson(normalizeStoredConclusion(data.conclusion, report.overallScore!))
      && hashCanonicalJson(report.references ?? []) === hashCanonicalJson(rebuilt.references);
  } catch { return false; }
}

/** Uploaded observations must still be the current frozen document bytes and
 * source witness admitted by the canonical scan, not only a plausible doc URL. */
function assessedDocumentsMatchSource(report: SiteRagReport, canonicalSed: SiteEvidenceDocument): boolean {
  return qualifiedLocalSentimentRows(report.publicSentimentResearch?.section).every(({ entry }) => {
    if (entry.sourceId !== 'uploaded-document') return true;
    const source = entry.documentSource;
    const doc = canonicalSed.uploadedDocuments?.find(row => row.docId === source?.documentId);
    const body = doc?.textContent;
    return !!source && typeof body === 'string' && sha256Hex(body) === source.bodySha256
      && body.slice(source.charStart, source.charEnd) === entry.sourcePassage
      && (canonicalSed.publicSentiment?.entries ?? []).some(row => row.sourceId === 'uploaded-document'
        && row.url === entry.url && row.sourcePassage === entry.sourcePassage
        && hashCanonicalJson(row.documentSource) === hashCanonicalJson(source));
  });
}

function completedChecks(report: Partial<SiteRagReport>, method: DeterministicScreeningValidation['method'] = 'source-bound-no-local-stance-v1'): boolean {
  const basis = report.screeningBasis;
  const scan = basis?.sentimentScan;
  const qa = report.qaFindings;
  if (!basis || !scan || !report.id || !report.sedId) return false;
  const noLocalStance = method === 'source-bound-no-local-stance-v1';
  const sourceContentMatches = noLocalStance
    ? report.publicSentimentStatus === 'unavailable' && report.sentimentIndex == null
      && stanceFields.every(key => !report[key])
      && (scan.outcome === 'coverage-only' || scan.localStanceBasis === 'no-dated-local-stance')
      && hashCanonicalJson(report.structuredFindings ?? []) === hashCanonicalJson(buildSentimentScreeningFindings(scan))
    : scan.outcome === 'evidence-screening' && scan.localStanceBasis === undefined && assessedConstructionMatches(report);
  return report.reportType === 'public-sentiment' && sourceContentMatches
    && citedSourcesAreAdmitted(report)
    && !report._metadataOnly && !report.degradedMode && !report.isDraft && report.generationTier !== 'draft'
    && report.qualityHold !== 'needs-revision' && !containsTruthViolation(qa)
    && Array.isArray(scan.sourceCoverage) && scan.sourceCoverage.every(row => row && typeof row.coverage === 'string' && Array.isArray(row.errors))
    && Array.isArray(scan.unreportedSources)
    && scan.version === 'sentiment-screening-v1' && hash.test(scan.queryFingerprint)
    && Number.isFinite(Date.parse(scan.fetchedAt)) && Array.isArray(scan.attemptedSources) && scan.attemptedSources.length > 0
    && basis.sed.id === report.sedId
    && (report.sedVersionUsed == null || basis.sed.version === report.sedVersionUsed)
    && typeof report.executiveSummary === 'string' && report.executiveSummary.length > 100
    && typeof report.sentimentAppendix === 'string' && report.sentimentAppendix.length > 100
    && typeof qa?.grounding?.clean === 'boolean' && qa.grounding.mode === 'on'
    && Array.isArray(qa.grounding.violations) && !qa.grounding.violations.some(row => row.severity === 'block')
    && Array.isArray(qa.waterUseScreening?.findings)
    && Array.isArray(qa.absenceAssertionAudit?.findings)
    && !(report.sanityChecks ?? []).some(row => !row.passed && row.severity === 'error');
}

/** Called only by the deterministic construction branch after final source
 * guards, never from a user-supplied selector or as a missing-review fallback. */
export function validateDeterministicScreening(
  report: SiteRagReport, sed: SiteEvidenceDocument, receipt: SentimentScreeningReceipt, canonicalSed: SiteEvidenceDocument = sed,
): DeterministicScreeningValidation | undefined {
  if (canonicalSed !== sed && (!report.publicSentimentResearch
    || hashCanonicalJson(report.publicSentimentResearch.section) !== hashCanonicalJson(sed.publicSentiment)
    || canonicalSed.id !== sed.id || canonicalSed.version !== sed.version)) return undefined;
  const ready = publicSentimentScreeningReadiness(sed, Date.parse(receipt.fetchedAt));
  const method = receipt.outcome === 'evidence-screening' && receipt.localStanceBasis === undefined
    ? 'source-bound-local-stance-v1' : 'source-bound-no-local-stance-v1';
  if (method === 'source-bound-local-stance-v1'
    && hashCanonicalJson(report.publicSentimentResearch?.section) !== hashCanonicalJson(sed.publicSentiment)) return undefined;
  if (!sed.publicSentiment || !ready.ready || !completedChecks(report, method)
    || (method === 'source-bound-local-stance-v1' && !assessedDocumentsMatchSource(report, canonicalSed))
    || hashCanonicalJson(ready.receipt) !== hashCanonicalJson(receipt)
    || hashCanonicalJson(report.screeningBasis?.sentimentScan) !== hashCanonicalJson(receipt)
    || report.sedId !== sed.id || report.screeningBasis?.sed.version !== sed.version) return undefined;
  const sections = extractSections(report);
  const references = report.references ?? [];
  if (!citedSourcesAreAdmitted(report, sed.publicSentiment)
    || checkPublicSentimentAssertions({ evidence: sed.publicSentiment, sections, references }).length
    || verifyWaterUseScreeningClaims(report, sed).some(row => row.severity === 'block')
    || extractAbsenceAssertionCandidates(report).length
    || checkDocumentFactCitations(report, sed).length > 0) return undefined;
  const contentHashVersion = method === 'source-bound-local-stance-v1' ? 'canonical-section-text-v3' : 'canonical-section-text-v2';
  return { schemaVersion: 'deterministic-screening-validation-v1', method,
    reportId: report.id, source: { sedId: sed.id, sedVersion: sed.version, snapshotHash: hashCanonicalJson(canonicalSed) },
    ...(report.publicSentimentResearch ? { researchHash: hashCanonicalJson(report.publicSentimentResearch) } : {}),
    scanHash: hashCanonicalJson(receipt), contentHash: contentHash(report, contentHashVersion), contentHashVersion, checkedAt: new Date().toISOString() };
}

/** Full-body verification is required. A metadata-only or stale receipt cannot
 * turn an ordinary unreviewed report into an accepted source product. */
export function hasValidatedDeterministicScreening(report: Partial<SiteRagReport>): boolean {
  const receipt = report.deterministicScreeningValidation;
  return !!receipt && !!receipt.source && typeof report.id === 'string' && report.id.length > 0
    && typeof report.sedId === 'string' && report.sedId.length > 0 && receipt.schemaVersion === 'deterministic-screening-validation-v1'
    && (receipt.method === 'source-bound-no-local-stance-v1' || receipt.method === 'source-bound-local-stance-v1')
    && (receipt.method !== 'source-bound-local-stance-v1' || receipt.contentHashVersion === 'canonical-section-text-v3')
    && receipt.reportId === report.id && receipt.source?.sedId === report.sedId
    && receipt.source.sedVersion === report.screeningBasis?.sed.version && hash.test(receipt.source.snapshotHash)
    && hash.test(receipt.scanHash) && hash.test(receipt.contentHash)
    && Number.isFinite(Date.parse(receipt.checkedAt)) && completedChecks(report, receipt.method)
    && (report.publicSentimentResearch ? receipt.researchHash === hashCanonicalJson(report.publicSentimentResearch) : receipt.researchHash === undefined)
    && receipt.scanHash === hashCanonicalJson(report.screeningBasis?.sentimentScan)
    && (receipt.contentHashVersion === undefined || receipt.contentHashVersion === 'canonical-section-text-v2' || receipt.contentHashVersion === 'canonical-section-text-v3')
    && receipt.contentHash === contentHash(report, receipt.contentHashVersion);
}
